import _ from "lodash";
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from 'recoil';
import { ToggleDocumentDownloadPack } from "../../../Component/Api/DocumentUpload/DocumentUploadHelper";
import { IndividualService } from "../../../Component/Api/Individual/IndividualService";
import { ConfirmationDialog } from "../../../Component/Common/Components/ConfirmationDialog";
import { IFilter } from '../../../Component/Common/Components/DataTable/Filters';
import { ColumnSortOption, SortDirections } from '../../../Component/Model/Dto/ColumnSortOption';
import { DocumentsForDownloadParameters } from "../../../Component/Model/Dto/Document";
import { EEScorecard } from "../../../Component/Model/Dto/EmploymentEquity/EEScorecard";
import { Individual } from "../../../Component/Model/Dto/Individual";
import { PeopleDtos } from "../../../Component/Model/Dto/People";
import { ExportKeyValue } from "../../../Component/Model/Dto/Upload";
import { DataImportType, EmployeeTypes, UserActivityActionType } from "../../../Component/Model/Enum/BEE123Enums";
import { EntriesPerPage, IBulkActions } from "../../../Component/Model/Table/Table";
import { defaultObject } from "../../../Component/Model/helpers";
import { ConsolidationAtom } from '../../../State/Consolidation.Atom';
import { ImportExportButtonsDialog } from "../../Common/ImportAndExport/ImportExportButtonsDialog";
import { IndividualModal } from "../../Common/Individual/IndividualModal";
import { SupportingDocuments } from '../../Common/SupportingDocuments/SupportingDocuments';
import { CheckPersonModal } from "./CheckPersonModal";
import { EmployeeModal } from "./EmployeeModal";
import { EmployeeStructureConsolidationWithActionsTableMapper, EmployeeStructureWithActionsTableMapper } from "./EmployeeStructureColumns";
import { EmployeeStructureDataTable } from './EmployeeStructureDataTable';
import { userRolesState } from "../../../State/user.Atom";
import config from "../../../config";
import { EE123PeopleRole_Edit } from "../../../Component/Common/Constants/UserRoles";
import { UserActivityService } from "../../../Component/Api/UserActivity/UserActivityService";
import { EmployeeStructureFullSection } from "../../../Component/Common/Constants/UserActivityTrackingSections";


interface IProps {
    scorecardId: string;
    GenderIds?: Array<number>;
    RaceIds?: Array<number>;
    OccupationIds?: Array<number>;
    scorecard: EEScorecard.DtoOut;
    hasFilters: boolean;
    filterByDate: boolean;
    filterDate: Date;
}

export const EmployeeStructureTableWithActions = (props: IProps) => {
    const userRoles = useRecoilValue(userRolesState);

    const [employeeStructureData, setEmployeeStructureData] = React.useState<Individual.SummaryDtoIn[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [showPersonCheckModal, setShowPersonCheckModal] = React.useState<boolean>(false);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [selectedDocumentId, setSelectedDocumentId] = React.useState<number>(0);
    const [documentsPopUp, setDocumentsPopUp] = React.useState<boolean>(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<number>(0);
    const [showIndividualModal, setShowIndividualModal] = React.useState<boolean>(false);
    const [showEmployeeModal, setShowEmployeeModal] = React.useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
    const link = 'IndividualRow';
    const [person, setPerson] = React.useState<PeopleDtos.GetPersonRequest | null>(null);
    const isConsolidationScorecard = useRecoilValue(ConsolidationAtom);
    const [identificationNumber, setIdentificationNumber] = React.useState<string>('');
    const [employeeCode, setEmployeeCode] = React.useState<string>('');
    const [searchValue, setSearchValue] = React.useState<string>("");
    const [sortColumnNames, setSortColumnNames] = React.useState<string[]>([]);
    const [sortDirections, setSortDirections] = React.useState<SortDirections[]>([]);
    const [filters, setFilters] = React.useState({});
    const canEdit = userRoles.includes(EE123PeopleRole_Edit);

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setSortColumnNames([sortOption.ColumnName]);
        setSortDirections([sortOption.SortDirection]);
        setPageNumber(1);
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let filteredRequest: Individual.PeopleSearchRequest = {
                PageNumber: pageNumber,
                PageSize: entriesPerPage.defaultValue!,
                ScorecardId: props.scorecardId,
                SearchValue: searchValue,
                SortColumnNames: sortColumnNames,
                SortDirections: sortDirections,
                GenderIds: props.GenderIds,
                OccupationIds: props.OccupationIds,
                RaceIds: props.RaceIds,
                DisabledValues: [],
                IsYesAbsorbed: [],
                IsYesEmployee: [],
                EmployeeTypeIds: [EmployeeTypes.Employee, EmployeeTypes.Employee_Previously_Unemployed],
                HasSupportingDocuments: false,
                EESearchEmployedAtDate: props.filterDate
            };

            filteredRequest = { ...filteredRequest, ...filters };

            if (filteredRequest.RaceIds?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["RaceIds"]);
            }

            if (filteredRequest.GenderIds?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["GenderIds"]);
            }

            if (filteredRequest.OccupationIds?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["OccupationIds"]);
            }

            if (filteredRequest.DisabledValues?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["DisabledValues"]);
            }

            if (filteredRequest.IsYesAbsorbed?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["IsYesAbsorbed"]);
            }

            if (filteredRequest.IsYesEmployee?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["IsYesEmployee"]);
            }

            if (filteredRequest.ProvinceTypeIds?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["ProvinceTypeIds"]);
            }

            if (filteredRequest.HireDateFilterStart?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["HireDateFilterStart"]);
            }

            if (filteredRequest.HireDateFilterEnd?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["HireDateFilterEnd"]);
            }

            if (filteredRequest.TerminationDateFilterStart?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["TerminationDateFilterStart"]);
            }

            if (filteredRequest.TerminationDateFilterEnd?.length === 0) {
                filteredRequest = _.omit(filteredRequest, ["TerminationDateFilterEnd"]);
            }

            if (filteredRequest.AgeRangeFilterMinimum === 0 || filteredRequest.AgeRangeFilterMinimum === undefined) {
                filteredRequest = _.omit(filteredRequest, ["AgeRangeFilterMinimum"]);
            }

            if (filteredRequest.AgeRangeFilterMaximum === 0 || filteredRequest.AgeRangeFilterMaximum === undefined) {
                filteredRequest = _.omit(filteredRequest, ["AgeRangeFilterMaximum"]);
            }

            if (filteredRequest.IsTemporary == null || filteredRequest.IsTemporary == false) {
                filteredRequest = _.omit(filteredRequest, ["IsTemporary"]);
            }

            let searchResult = await IndividualService.PeopleSearch(filteredRequest);

            if (searchResult.data) {
                setEmployeeStructureData(searchResult.data.dataObject);
                setFilteredItemsCount(searchResult.data.filteredItemsCount);
                setTotalItemsCount(searchResult.data.totalItemsCount);
            }
        } catch (error) {
            setEmployeeStructureData(defaultObject);
            setFilteredItemsCount(1);
            setTotalItemsCount(1);
        } finally {
            setIsLoading(false);
        }

    };

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedEmployeeId(0);
    }

    const handleEdit = (id: number) => {
        setSelectedEmployeeId(id);
        setShowIndividualModal(true);
    }

    const handleConfirmDelete = (id: number) => {
        setSelectedEmployeeId(id);
        setShowConfirmationDialog(true);
    }

    const handleDelete = async () => {

        try {
            setIsLoading(true);
            const result = await IndividualService.DeleteIndividual(selectedEmployeeId, false, props.scorecardId);
            if (result.data === '') {
                toast.success('Employee deleted successfully');
                fetchData();
            }
            else {
                toast.error("Cant delete - " + result.data.toString());
            }

        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
        handleCloseConfirmation();

    }

    const closeIndividualModal = () => {
        setShowIndividualModal(false);
        setShowPersonCheckModal(false);
        setSelectedEmployeeId(0);
        fetchData();
    }

    const closeEmployeeModal = () => {
        setShowEmployeeModal(false);
        setShowPersonCheckModal(false);
        setSelectedEmployeeId(0);
        fetchData();
    }

    const handleAttachedSD = (id: number) => {
        setSelectedDocumentId(id);
        setDocumentsPopUp(true);
    }

    const handleToggleDocumentPack = async (id: number) => {

        let added = employeeStructureData.find(item => item.IndividualId === id)?.DocumentsSelectedForDownload;
        setSelectedEmployeeId(id);
        if (added !== undefined) {

            const toggleDocumentsParamters: DocumentsForDownloadParameters = {
                entityId: id.toString(),
                scorecardId: props.scorecardId,
                link: link
            };

            await ToggleDocumentDownloadPack(toggleDocumentsParamters, added);
            fetchData();
        }

    }

    const onDocumentPopupClose = () => {
        setDocumentsPopUp(false);
        fetchData();
    }

    const onEmployeeAdd = () => {
        if (config.enable_central_management === 'true') {
            setShowEmployeeModal(true);
        } else {
            setShowPersonCheckModal(true);
        }
    }

    const afterEmployeeSave = (id: number) => {
        handleEdit(id);
        setShowEmployeeModal(false);
        setShowPersonCheckModal(false);
    }


    const setPersonFromModal = (foundPerson: PeopleDtos.GetPersonRequest | null) => {
        if (foundPerson == null) {
            setSelectedEmployeeId(0);
        }
        setPerson(foundPerson);
        setShowPersonCheckModal(false);
        setShowEmployeeModal(true);

    }
    const setIdAndEmployeeCodeFromModal = (id: string, employeeCode: string) => {
        setIdentificationNumber(id);
        setEmployeeCode(employeeCode);
    }

    const bulkActions: IBulkActions[] = [
        ...(canEdit ? [{
            label: 'Capture new Employee',
            action: onEmployeeAdd
        }] : [])
    ];    

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, searchValue, sortColumnNames, sortDirections, filters, props.filterDate]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
          UserActivityService.createActivityLog({
            section: EmployeeStructureFullSection,
            actionType: UserActivityActionType.View,
            actionDescription: "View employee structure",
            scorecardId: props.scorecardId,
            requestUrl: window.location.href
        });
    }, [])

    const parameters: ExportKeyValue[] = [
        {
            key: "ScorecardId",
            value: props.scorecardId
        },
        {
            key: "Sampled",
            value: false
        },
        {
            key: "FilterDate",
            value: props.filterDate
        }

    ];


    const employeeStructureFilters: Array<IFilter> = [
        {
            label: 'Gender:',
            values: [{ key: 'Male', val: 1 }, { key: 'Female', val: 2 }],
            type: 1,
            propname: 'GenderIds',
        },
        {
            label: 'Race:',
            values: [{ key: 'African', val: 1 }, { key: 'Coloured', val: 2 }, { key: 'Indian', val: 3 }, { key: 'White', val: 4 }, { key: 'Non South African', val: 5 }],
            type: 1,
            propname: 'RaceIds',
        },
        {
            label: 'Occupation Level:',
            values: [{ key: 'Executive Director', val: 1 },
            { key: 'Other Executive Management', val: 4 },
            { key: 'Senior Management', val: 5 },
            { key: 'Middle Management', val: 6 },
            { key: 'Junior Management', val: 7 },
            { key: 'Semi skilled', val: 8 },
            { key: 'Unskilled', val: 9 }],
            type: 1,
            propname: 'OccupationIds'
        },
        {
            label: 'Disabled:',
            values: [{ key: 'Yes', val: true }, { key: 'No', val: false }],
            type: 1,
            propname: 'DisabledValues'
        },
        {
            label: 'Province:',
            values: [
                { key: 'Unspecified', val: 0 },
                { key: 'National (NAT)', val: 1 },
                { key: 'Eastern Cape (EC)', val: 2 },
                { key: 'Free State (FS)', val: 3 },
                { key: 'Gauteng (GP)', val: 4 },
                { key: 'KwaZulu-Natal (KZN)', val: 5 },
                { key: 'Limpopo (LP)', val: 6 },
                { key: 'Mpumalanga (MP)', val: 7 },
                { key: 'North West (NW)', val: 8 },
                { key: 'Northern Cape (NC)', val: 9 },
                { key: 'Western Cape (WC)', val: 10 },
            ],
            type: 1,
            propname: 'ProvinceTypeIds'
        },
        {
            label: 'Y.E.S. Employee:',
            values: [{ key: 'Yes', val: true }, { key: 'No', val: false }],
            type: 1,
            propname: 'IsYesEmployee'

        },
        {
            label: 'Y.E.S. Absorbed:',
            values: [{ key: 'Yes', val: true }, { key: 'No', val: false }],
            type: 1,
            propname: 'IsYesAbsorbed'
        },
        {
            label: 'Start Period',
            values: [],
            type: 3,
            propname: 'HireDateFilter',

        },
        {
            label: 'Termination Period',
            values: [],
            type: 3,
            propname: 'TerminationDateFilter',

        },
        {
            label: 'Age Range',
            values: [],
            type: 6,
            propname: 'AgeRangeFilter',

        },
        {
            label: 'Only include temporary employees ',
            values: [],
            type: 7,
            propname: 'IsTemporary',
        }
    ];

    const filtersChanged = (filterProps: any) => {
        let HireDateFilterStart = filterProps['HireDateFilter']?.start;
        let HireDateFilterEnd = filterProps['HireDateFilter']?.end;
        let TerminationDateFilterStart = filterProps['TerminationDateFilter']?.start;
        let TerminationDateFilterEnd = filterProps['TerminationDateFilter']?.end;
        let AgeRangeFilterMinimum = filterProps['AgeRangeFilter']?.minimum;
        let AgeRangeFilterMaximum = filterProps['AgeRangeFilter']?.maximum;

        setFilters({ ...filterProps, HireDateFilterStart, HireDateFilterEnd, TerminationDateFilterStart, TerminationDateFilterEnd, AgeRangeFilterMinimum, AgeRangeFilterMaximum });
    }

    let employeeTableData = undefined;
    if (isConsolidationScorecard) {
        employeeTableData = EmployeeStructureConsolidationWithActionsTableMapper(employeeStructureData, handleEdit, handleConfirmDelete, handleAttachedSD, handleToggleDocumentPack);

    } else {
        employeeTableData = EmployeeStructureWithActionsTableMapper(employeeStructureData, handleEdit, handleConfirmDelete, handleAttachedSD, handleToggleDocumentPack);
    }
    return (
        <>
            <EmployeeStructureDataTable
                employeeStructureData={employeeTableData!}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                searchValueChanged={searchValueChanged}
                columnSortingChanged={columnSortingChanged}
                isLoading={isLoading}
                hasFilters={props.hasFilters}
                employeeStructureFilters={employeeStructureFilters}
                filtersChanged={filtersChanged}
                bulkActions={bulkActions}
            />

            {documentsPopUp && <SupportingDocuments display={documentsPopUp} handleCancel={() => onDocumentPopupClose()} link={link} id={selectedDocumentId.toString()} 
                scorecardId={props.scorecardId} isEEModule={true} section={EmployeeStructureFullSection}/>}

            {showIndividualModal && <IndividualModal showIndividualModal={showIndividualModal} closeIndividualModal={closeIndividualModal} scorecardId={props.scorecardId} 
                id={selectedEmployeeId} section={EmployeeStructureFullSection}/>}

            {showPersonCheckModal && <CheckPersonModal scorecardId={props.scorecardId} showPersonCheckModal={showPersonCheckModal} handleEdit={handleEdit} closePersonCheckModal={() => { setShowPersonCheckModal(false) }} setPerson={setPersonFromModal} setIdAndEmployeeCode={setIdAndEmployeeCodeFromModal}></CheckPersonModal>}

            {showEmployeeModal && <EmployeeModal scorecardId={props.scorecardId} person={person} afterSaved={afterEmployeeSave} showEmployeeModal={showEmployeeModal} 
                closeEmployeeModal={closeEmployeeModal} identificationNumber={identificationNumber} employeeCode={employeeCode} section={EmployeeStructureFullSection}/>}

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={handleDelete}
                handleCancel={handleCloseConfirmation}
                heading={"Delete Entry"}
                message={"Are you sure you want to delete the selected entry?"}
                isLoading={isLoading}
            />


            {config.enable_central_management === 'true'?
                <ImportExportButtonsDialog
                    scorecardId={props.scorecardId}
                    uploadType={DataImportType[DataImportType.EmployeeStructure]}
                    controlsEnabled={true}
                    showHelpfileDownload={true}
                    importBus={false}
                    coreExportEnabled={false}
                    coreImportEnabled={true}
                    type={"EmployeeStructure"}
                    typeId={DataImportType.Individual}
                    parameters={parameters}
                    reloadTable={fetchData}
                    disableImport={!canEdit} 
                    section="Employees"/>
                :
                <ImportExportButtonsDialog
                    scorecardId={props.scorecardId}
                    uploadType={DataImportType[DataImportType.EmployeeStructure]}
                    controlsEnabled={true}
                    showHelpfileDownload={true}
                    importBus={false}
                    coreExportEnabled={false}
                    coreImportEnabled={false}
                    type={"EmployeeStructure"}
                    typeId={DataImportType.EmployeeStructure}
                    parameters={parameters}
                    reloadTable={fetchData}
                    disableImport={!canEdit} />
            }
        </>
    )
}
